/**
 * fetchLivePrices - Fetches live prices for a list of product handles based on the user's country code.
 *
 * @param {Array<string>} handles - An array of product handles to fetch prices for.
 * @param {string} countryCode - The country code used to set the context for the GraphQL query.
 * @returns {Promise<Array<Object>>} - A promise that resolves to an array of product data, 
 *                                      each containing handle, title, and price range information.
 *
 * The function constructs a GraphQL query to retrieve product details, including the minimum 
 * and maximum variant prices for each product handle provided. It sends a POST request to the 
 * Shopify Storefront API and returns the product data in a structured format.
 */
export const fetchLivePrices = async (handles, countryCode) => {
  if (!handles?.length) return [];

  const query = `
      query getProducts @inContext(country: ${countryCode}) {
        ${handles
      .map(
        (handle, i) => ` 
          p${i}: product(handle: "${handle}") {
            handle
            title
            priceRange {
              minVariantPrice { amount currencyCode }
              maxVariantPrice { amount currencyCode }
            }
          }
        `
      )
      .join('\n')}
      }
    `;

  const res = await fetch('https://lineage-journey.myshopify.com/api/2023-10/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': 'e3e6f7f2ffec6ef5ba855eaaeb608d83',
    },
    body: JSON.stringify({ query }),
  });

  const { data } = await res.json();

  return data ? Object.values(data) : [];
};

/**
 * fetchLivePrice - Fetches the live price details of a specific product based on its handle and the user's country code.
 *
 * @param {string} handle - The unique handle of the product to fetch.
 * @param {string} countryCode - The country code used to set the context for the GraphQL query.
 * @returns {Promise<Object|null>} - A promise that resolves to the product data containing handle, title, and price range information,
 *                                    or null if the product is not found.
 *
 * The function constructs a GraphQL query to retrieve the product details, including the minimum and maximum variant prices.
 * It sends a POST request to the Shopify Storefront API and returns the product data in a structured format.
 */
export const fetchLivePrice = async (handle, countryCode) => {
  const query = `
      query getProduct @inContext(country: ${countryCode}) {
        product(handle: "${handle}") {
          handle
          title
          priceRange {
            minVariantPrice { amount currencyCode }
            maxVariantPrice { amount currencyCode }
          }
        }
      }
    `;

  try {
    const res = await fetch('https://lineage-journey.myshopify.com/api/2023-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': 'e3e6f7f2ffec6ef5ba855eaaeb608d83',
      },
      body: JSON.stringify({ query }),
    });

    const { data } = await res.json();
    return data?.product || null;
  } catch (error) {
    console.log(error);
  }
};
